import React, { useEffect, useState } from "react";

import MinimizeIcon from "@uui/Minimize01";
import {
  filter,
  groupBy,
  isEmpty,
  map,
  partition,
  sortedUniq,
  sumBy,
} from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import UploadTutorial from "@app/analytics/TutorialsAndPopups/UploadTutorial";
import useUpload from "@app/analytics/upload/useUpload";
import useUploadStore from "@app/analytics/upload/useUploadStore";
import { validateFileDurations } from "@app/analytics/upload/utils";
import useAuth from "@app/common/auth/useAuth";
import { Toast, toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import {
  formattedListWithConjunction,
  pluralize,
  pluralizePhrase,
} from "@app/common/utils";

import Button from "@rui/Button";
import FileUpload from "@rui/FileUpload";
import type { FileCandidate } from "@rui/FileUpload/types";
import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";
import Modal from "@rui/Modal";
import ModalHeader from "@rui/Modal/ModalHeader";
import { Column, Row } from "@rui/Stack";

import { ALLOWED_TYPES, MAX_FILE_GB, MAX_FILE_SIZE_MB } from "./constants";
import CreditsBanner from "./CreditsBanner";
import EditMetadataModal from "./EditMetadataModal";
import ProcessingUploadsModal from "./ProcessingUploadsModal";
import ProgressRow from "./ProgressRow";
import type { Upload, UploadMetadata } from "./types";
import UploadWidget from "./UploadWidget";

const ERROR_TOASTS = {
  insufficient_credits: {
    title: "Quota exceeded",
    message:
      "The uploaded files exceed your remaining minutes. Please purchase more credits, or remove files that exceed your quota to continue with processing.",
  },
  unknown_error: {
    title: "File upload error",
    message:
      "Something went wrong while uploading your file. Please remove the file and try again.",
  },
  unsupported_type: {
    title: "File type not supported",
    message: (fileNames: string[]) =>
      `${formattedListWithConjunction(
        fileNames
      )} cannot be uploaded. Please try again with ${formattedListWithConjunction(
        ALLOWED_TYPES,
        "or"
      )} file types.`,
  },
  file_too_large: {
    title: "File too large",
    message: (fileNames: string[]) =>
      `${formattedListWithConjunction(fileNames)} ${pluralize(
        "exceeds",
        "exceed",
        fileNames.length
      )} the maximum file size of ${MAX_FILE_GB}GB.`,
  },
  combo_unsupported_too_large: {
    title: "Invalid files",
    message: `All files must be of ${formattedListWithConjunction(
      ALLOWED_TYPES,
      "or"
    )} file type and ${MAX_FILE_GB}GB or smaller`,
  },
};

const preventExit = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

const UploadModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadTutorialOpen, setUploadTutorialOpen] = useState(false);
  const [editMetadataModalOpen, setEditMetadataModalOpen] = useState(false);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [uploadToEdit, setUploadToEdit] = useState<Upload | null>(null);

  const { fetchAndSetUser } = useAuth();
  const { userSettings } = useUserSettings();
  const { uploadCredits, subscriptionIsError, subscription } =
    useBillingContext();
  const toggleUploadTutorial = () => setUploadTutorialOpen((open) => !open);

  const totalMinutesRemaining =
    uploadCredits.monthlyBalance + uploadCredits.purchasedBalance;

  const inTrial = subscription.inTrialPeriod;

  const {
    uploads,
    createUpload,
    cancelAllUploads,
    cancelUpload,
    errorUploads,
    inProgressUploads,
    updateUploadMeta,
    completedUploads,
    modal,
    openModal,
    closeModal,
    clearUploads,
  } = useUploadStore((state) => ({
    uploads: state.uploads,
    createUpload: state.createUpload,
    cancelUpload: state.cancelUpload,
    cancelAllUploads: state.cancelAllUploads,
    errorUploads: filter({ status: "error" }, state.uploads),
    inProgressUploads: filter({ status: "loading" }, state.uploads),
    updateUploadMeta: state.updateUploadMeta,
    completedUploads: filter({ status: "complete" }, state.uploads),
    modal: state.modal,
    openModal: state.openModal,
    closeModal: state.closeModal,
    clearUploads: state.clearUploads,
  }));

  // Init open if in query params
  useEffect(() => {
    if (searchParams.get("modal") === "upload") {
      openModal();
      // Delete the parameter since we only use it to init the modal state
      // in the future, might be nice to have the param control the modal state
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("modal");
      setSearchParams(newSearchParams);
    }
  }, []);

  const totalUploadDuration = sumBy((upload) => upload.duration ?? 0, uploads);

  const effectiveMinutesRemaining = Math.floor(
    totalMinutesRemaining - totalUploadDuration
  );

  const errorReasons = sortedUniq(
    Object.keys(
      groupBy("error_reason", errorUploads)
    ) as Upload["errorReason"][]
  );

  const showErrorToast = (values: Toast) =>
    toast.error({ type: "system", ...values });

  useEffect(() => {
    if (inProgressUploads.length > 0 || completedUploads.length > 0) {
      window.addEventListener("beforeunload", preventExit);
    } else {
      window.removeEventListener("beforeunload", preventExit);
    }
    return () => window.removeEventListener("beforeunload", preventExit);
  }, [inProgressUploads.length, completedUploads.length]);

  useEffect(() => {
    if (errorReasons.includes("insufficient_credits")) {
      showErrorToast(ERROR_TOASTS["insufficient_credits"]);
    } else if (errorReasons.includes("unknown_error")) {
      showErrorToast(ERROR_TOASTS["unknown_error"]);
    }
  }, [
    errorReasons.includes("insufficient_credits"),
    errorReasons.includes("unknown_error"),
  ]);

  const { generateReports } = useUpload();

  const handleCancel = () => {
    cancelAllUploads();
    closeModal();
  };

  const handleGenerate = () => {
    const completedUploads: Upload[] = filter({ status: "complete" }, uploads);
    generateReports.mutate(
      { uploads: completedUploads },
      {
        onSuccess: () => {
          closeModal();
          clearUploads();
          setProcessingModalOpen(true);
          // refetch the user/billing account to get the updated credit balance
          fetchAndSetUser();
        },
        onError: () => {
          showErrorToast({
            title: "Unable to generate reports",
            message:
              "Something went wrong and your reports could not be generated at this time.",
          });
        },
      }
    );
  };

  const handleChange = async (fileCandidates: FileCandidate[]) => {
    const [validFiles, invalidFiles] = partition(
      { isValid: true },
      fileCandidates
    );

    if (!isEmpty(invalidFiles)) {
      const [largeFiles, invalidTypeFiles] = partition(
        { invalidReason: "too_large" },
        invalidFiles
      );

      if (isEmpty(largeFiles) && !isEmpty(invalidTypeFiles)) {
        // show the unsupported type toast if all the failures are due to invalid file types
        showErrorToast({
          title: ERROR_TOASTS.unsupported_type.title,
          message: ERROR_TOASTS.unsupported_type.message(
            map("file.name", invalidTypeFiles)
          ),
        });
      } else if (!isEmpty(largeFiles) && isEmpty(invalidTypeFiles)) {
        // show the too large toast if all the failures are due to the file size
        showErrorToast({
          title: ERROR_TOASTS.file_too_large.title,
          message: ERROR_TOASTS.file_too_large.message(
            map("file.name", largeFiles)
          ),
        });
      } else {
        // otherwise, show a more generic toast
        // TODO: remove this toast and instead just show both of the
        //  above toasts when we can support multiple system toasts
        showErrorToast(ERROR_TOASTS.combo_unsupported_too_large);
      }
    }

    const filesWithDuration = await validateFileDurations(
      validFiles,
      effectiveMinutesRemaining
    );

    filesWithDuration.forEach((fileCandidate) => createUpload(fileCandidate));
  };

  const handleOpenEdit = (upload: Upload) => () => {
    setUploadToEdit(upload);
    setEditMetadataModalOpen(true);
  };

  const handleCloseEdit = () => {
    setUploadToEdit(null);
    setEditMetadataModalOpen(false);
  };

  const handleConfirmEdits = (
    uploadLocalId: Upload["localId"],
    newOptions: UploadMetadata
  ) => {
    updateUploadMeta(uploadLocalId, newOptions);
  };

  return (
    <>
      <UploadWidget />
      <UploadTutorial
        open={uploadTutorialOpen}
        onClose={toggleUploadTutorial}
      />
      <ProcessingUploadsModal
        open={processingModalOpen}
        onClose={() => setProcessingModalOpen(false)}
      />
      <Modal open={modal.open} sx={{ padding: "0px" }} onClose={closeModal}>
        <Column sx={{ px: 3, pt: 1.5 }}>
          <ModalHeader
            title="Upload video & audio"
            onClose={closeModal}
            renderIcon={inProgressUploads.length > 0 ? <MinimizeIcon /> : null}
            sx={{ mb: "4px" }}
          />
          <Column spacing="12px">
            {subscriptionIsError ? (
              <InfoPanel severity="error" sx={{ width: "100%" }}>
                Error loading upload credits. Please try again later.
              </InfoPanel>
            ) : inTrial && totalMinutesRemaining <= 0 ? (
              <InfoPanel severity="info">
                Free trials don't include upload credits, but you may{" "}
                <Link to={ROUTES.BILLING_CHECKOUT_CREDITS} underline>
                  purchase them separately
                </Link>{" "}
                or{" "}
                <Link to={ROUTES.BILLING_COMPARE_PLANS} underline>
                  upgrade to a paid plan
                </Link>
                .
              </InfoPanel>
            ) : (
              <CreditsBanner
                onLearnMore={toggleUploadTutorial}
                minRemaining={effectiveMinutesRemaining}
              />
            )}
            <Column
              spacing={1}
              sx={{ maxHeight: "400px", overflowY: "scroll", pb: 1.5 }}
            >
              <FileUpload
                name="files"
                onChange={handleChange}
                sx={{
                  height: uploads.length ? "151px" : "246px",
                  padding: "16px",
                }}
                description="MP4 or M4A, maximum 5GB each"
                types={ALLOWED_TYPES}
                maxFileSizeMB={MAX_FILE_SIZE_MB}
                disabled={totalMinutesRemaining <= 0}
                disabledCta={{
                  label: inTrial ? "Purchase Credits" : "Get more credits",
                  href: ROUTES.BILLING_CHECKOUT_CREDITS,
                  color: inTrial ? "primary" : "secondary",
                }}
              />
              {uploads.length > 0 && (
                <>
                  <Divider sx={{ mb: 1 }} />
                  <Typography
                    variant="h3"
                    color="shades.neutral.700"
                    sx={{ mb: 0.5 }}
                  >
                    Upload Progress
                  </Typography>
                  {effectiveMinutesRemaining < 0 && (
                    <InfoPanel
                      severity="error"
                      title={ERROR_TOASTS.insufficient_credits.title}
                      sx={{ flexShrink: 0 }}
                    >
                      {ERROR_TOASTS.insufficient_credits.message}
                    </InfoPanel>
                  )}
                  {uploads.map((upload) => (
                    <ProgressRow
                      key={upload.id}
                      title={upload.name}
                      progress={upload.progress}
                      status={upload.status}
                      duration={upload.duration}
                      onEdit={handleOpenEdit(upload)}
                      onCancel={() => cancelUpload(upload.id)}
                      startTime={upload.startTime}
                      size={upload.sizeInBytes ?? null}
                    />
                  ))}
                </>
              )}
            </Column>
          </Column>
        </Column>
        <Column
          width="100%"
          spacing={1}
          sx={{
            borderTop: "1px solid rgba(0,0,0,0.2)",
            padding: "12px 24px",
          }}
        >
          {!userSettings.transcriptionOptIn && (
            <InfoPanel severity="warning">
              Warning: You currently have transcription disabled, which will
              affect any uploaded reports.{" "}
              <Link
                to={ROUTES.USER_SETTINGS_RECORDING_AND_PLAYBACK}
                onClick={closeModal}
              >
                View or change this setting
              </Link>
              .
            </InfoPanel>
          )}
          <Row variant={uploads.length ? "apart" : "right"}>
            {uploads.length > 0 && (
              <Typography variant="bodySecondaryReg" color="shades.neutral.500">
                {pluralizePhrase(uploads.length, "file")} (
                {pluralizePhrase(Math.ceil(totalUploadDuration), "minute")})
              </Typography>
            )}
            <Row variant="right" spacing={2}>
              <Button variant="text" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={
                  inProgressUploads.length > 0 ||
                  completedUploads.length === 0 ||
                  uploads.length === 0 ||
                  effectiveMinutesRemaining < 0
                }
                loading={generateReports.isPending}
              >
                Generate Reports
              </Button>
            </Row>
          </Row>
        </Column>
      </Modal>
      {uploadToEdit && uploads.length > 0 && (
        <EditMetadataModal
          open={editMetadataModalOpen}
          onClose={handleCloseEdit}
          onConfirm={handleConfirmEdits}
          upload={uploadToEdit}
        />
      )}
    </>
  );
};

export default UploadModal;
